import { useRef } from "react"
import { keepPreviousData, useQuery } from "@tanstack/react-query"
import type { GlobalFilters, TileQueryRequest } from "@csbi/contracts"
import { ApiError } from "../../../shared/api/request.js"
import { buildTileRequest } from "../api.js"
import { queryTileBatched } from "../batch.js"
import { createTileFingerprint } from "../tile-fingerprint.js"

export type TileSpec = {
  tileKey: string
  metrics: readonly string[]
  splitBy?: readonly TileQueryRequest["splitBy"][number][]
  topN?: number
  /**
   * ⚠ **Only for metrics that exist at one granularity and nowhere else.** `mau` is collected per calendar
   * month; asked for by day it comes back **null rather than empty**, so a tile that merely showed nothing
   * would look like a customer with no activity. Everything else follows the filter bar, because a screen
   * whose charts silently use different time buckets cannot be compared across cards.
   */
  granularity?: TileQueryRequest["granularity"]
}

export function useTileQuery(tile: TileSpec, filters: GlobalFilters, enabled = true) {
  const request = buildTileRequest(tile, filters)
  const fingerprint = createTileFingerprint(request)

  /**
   * ⚠ **The refresh button has to ask for fresh data, not merely ask again.**
   */
  const wantsFresh = useRef(false)

  const query = useQuery({
    queryKey: ["tiles", tile.tileKey, fingerprint],
    queryFn: ({ signal }) => {
      const fresh = wantsFresh.current
      wantsFresh.current = false
      return queryTileBatched(fresh ? buildTileRequest(tile, filters, { refresh: true }) : request, signal)
    },
    enabled: enabled && tile.metrics.length > 0,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
    retry: (n, err) => !(err instanceof ApiError && err.status < 500) && n < 2,
  })

  return {
    ...query,
    refetchFresh: () => {
      wantsFresh.current = true
      return query.refetch()
    },
    dimmed: query.isFetching && query.data != null,
    errorMessage: query.error instanceof ApiError ? query.error.message : query.error ? "Request failed" : undefined,
  }
}
